import {
  MAX_PAGE_SIZE,
  type Paginated,
  type SubscriptionProduct,
  type SubscriptionProductsListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import {
  type ServerSelectQueryArgs,
  type UseServerSelectQuery,
  type UseServerSelectResolve,
} from '@/shared/components/selection/types/server-selection.types';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The query as it goes ON THE WIRE — the schema's INPUT, not its output.
 *
 * `keys` is the difference: the contract parses one comma-joined parameter into
 * an array, so the parsed type is `string[]` while the thing to send is
 * `'a,b,c'`. Typing the request by the output would make RTK Query serialize
 * `?keys=a&keys=b`, which `qs` hands the api as an array — exactly the ambiguity
 * `ProductKeysParamSchema` exists to remove (N7).
 */
type ProductLookupParams = z.input<typeof SubscriptionProductsListQuerySchema>;

/**
 * The **cross-module read bridge** for subscription plans (W6).
 *
 * Every surface outside `modules/Subscriptions` that has to name a plan — the
 * order desk's product picker, the orders queue's plan column, the Courses
 * discount-rule scope — reads it through here rather than importing
 * `modules/Subscriptions/services/subscriptions.api`. That import is banned in
 * both directions: `shared/` may never reach into a module (W2), and one module
 * may never reach into another's api (W6).
 *
 * ## It resolves by KEY, and that is why it could not exist before
 *
 * The catalogue is the only place a plan is known by its **id**. Everything else
 * stores the **key** — `direct_payment_orders.product_key`, a discount rule's
 * `appliesToProductKeys`, every entry in `billing.directPrices` — so the `ids`
 * filter the list already carried could offer plans and never resolve one a row
 * was holding. P23b2 added `keys` to the filter spec and to the list contract
 * for exactly this, and this bridge is its first consumer. A lookup with no
 * consumer would have been the "hook nothing calls is a lie in the inventory"
 * that kept it from being built in P23b1.
 *
 * It is read-only on purpose: writing a plan is the owning module's business,
 * and a bridge that could mutate would be a second write path around the events
 * that module publishes (W5). Staying in step is `lookup-invalidation`'s job,
 * over `billing.product.changed`.
 */
export const subscriptionProductLookupApi = createApi({
  reducerPath: 'subscriptionProductLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ProductLookup'],
  endpoints: (builder) => ({
    lookupProducts: builder.query<Paginated<SubscriptionProduct>, ProductLookupParams>({
      query: (params) => ({ url: '/admin/subscription-products', method: 'GET', params }),
      providesTags: ['ProductLookup'],
    }),
  }),
});

export const { useLookupProductsQuery } = subscriptionProductLookupApi;

/**
 * The **name** an operator reads, over the **key** a row actually stores.
 *
 * Both, not one: two plans can share a name across a rename, and the key is what
 * an operator will find again in `billing.directPrices` or in a support thread.
 * `name` arrives already resolved to the caller's locale (N5).
 */
function toItem(product: SubscriptionProduct): SelectionOption {
  return { value: product.key, label: product.name, description: product.key };
}

const EMPTY: readonly SelectionOption[] = [];

/**
 * Adapts the bridge to `ServerSelect`'s option-page contract, narrowed to the
 * kinds a caller can use. The SERVER narrows, so every page the picker scrolls
 * through is a full one — a client filter over a page of twenty would hand back
 * however many of them happened to be the right kind.
 */
function makeProductOptionsQuery(
  narrowing: Pick<ProductLookupParams, 'kind'>,
): UseServerSelectQuery {
  const useOptions: UseServerSelectQuery = ({ search, page, pageSize }: ServerSelectQueryArgs) => {
    const { data, isFetching, isError, refetch } = useLookupProductsQuery({
      ...narrowing,
      page,
      pageSize,
      // The paywall's own order, which staff arranged — never the primary key.
      // A picker that listed plans by insertion order would put the plan somebody
      // is looking for wherever it happened to be created.
      sort: 'sortOrder',
      order: 'asc',
      ...(search === '' ? {} : { search }),
    });

    return {
      items: data === undefined ? EMPTY : data.items.map(toItem),
      hasNext: data?.meta.hasNext ?? false,
      isFetching,
      isError,
      // So a failed load offers a way out rather than a dead-end message (U0).
      refetch,
    };
  };

  return useOptions;
}

/** Every kind — the order desk records a plan and a fee through the same door. */
export const useProductOptionsQuery: UseServerSelectQuery = makeProductOptionsQuery({});

/**
 * Plans only (D9b2): a comp grants whatever a plan grants, and a fee grants
 * nothing — `EntitlementGrantsService` refuses one, so the picker never offers it.
 */
export const usePlanOptionsQuery: UseServerSelectQuery = makeProductOptionsQuery({
  kind: 'subscription',
});

/**
 * Resolves saved product keys that may be on no page at all.
 *
 * A key the catalogue no longer holds — a withdrawn plan an old order still
 * names — comes back **absent**, deliberately. Inventing an option for it here
 * would put a plan nobody sells back into the dropdown; the kit's own `labelOf`
 * already falls back to the raw value when a chip has no option, which is the
 * layer that fallback belongs to, and a table draws its own in its column.
 */
export const useResolveProductKeys: UseServerSelectResolve = (keys: readonly string[]) => {
  const { data, isFetching } = useLookupProductsQuery(
    {
      page: 1,
      // Clamped, because the api REFUSES an over-large page rather than clamping
      // it (N7): a rule holding more saved keys than one page can carry would
      // 422 every resolve and name none of them. `useServerOptions` slices to
      // the same ceiling; this is the bridge's own half of the guard.
      pageSize: Math.min(Math.max(keys.length, 1), MAX_PAGE_SIZE),
      sort: 'sortOrder',
      order: 'asc',
      keys: keys.join(','),
    },
    // Nothing to resolve is not an empty request — it is no request.
    { skip: keys.length === 0 },
  );

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    isFetching,
  };
};
